import { api } from "../api";
import { showAssignmentDetail } from "../components/assignment-detail";
import { hasPortalFeature } from "../utils/portal-features";
import { Icons } from "../utils/icons";
import type { StudentProfil, StudentPlanungData, StudentAssignment, StudentHours } from "../types";
import { esc } from "../utils/html";

function renderProgressBar(completed: number, required: number, color: string): string {
  const pct = required > 0 ? Math.min((completed / required) * 100, 100) : 0;
  return `<div style="background:#e5e7eb;border-radius:9999px;height:10px;overflow:hidden">
    <div style="background:${color};width:${pct}%;height:100%;border-radius:9999px;transition:width .3s"></div>
  </div>`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

function todayFormatted(): string {
  return formatDate(new Date().toISOString().split("T")[0]);
}

function renderCurrentAssignment(a: StudentAssignment): string {
  const hasComment = !!(a.studentNote && a.studentNote.trim());
  const clickable = !!a.practiceId || hasComment;
  const commentHint = hasComment
    ? `<div style="margin-top:.75rem;display:flex;align-items:center;gap:.4rem;font-size:.85rem;font-weight:600;color:#2563eb">
        <span class="assignment-comment-icon">${Icons.chatBubbleLeftRight}</span>Hinweis von der Schule
      </div>`
    : "";
  return `<div class="card" data-assignment-id="${a.id}" style="border-left:4px solid ${a.departmentColor};padding:1.25rem${clickable ? ";cursor:pointer" : ""}">
    <div style="font-size:.75rem;font-weight:600;text-transform:uppercase;letter-spacing:.05em;color:${a.departmentColor};margin-bottom:.5rem">${esc(a.departmentName)}</div>
    <div style="font-size:1.1rem;font-weight:700;margin-bottom:.5rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(a.practiceName || "Freier Eintrag")}</div>
    ${a.practiceAddress ? `<div style="font-size:.875rem;color:#6b7280;margin-bottom:.25rem;overflow-wrap:break-word">📍 ${esc(a.practiceAddress)}</div>` : ""}
    ${a.practicePhone ? `<div style="font-size:.875rem;color:#6b7280;margin-bottom:.25rem">📞 <a href="tel:${esc(a.practicePhone)}" style="color:inherit">${esc(a.practicePhone)}</a></div>` : ""}
    ${a.practiceContact ? `<div style="font-size:.875rem;color:#6b7280;overflow-wrap:break-word">👤 ${esc(a.practiceContact)}</div>` : ""}
    ${commentHint}
    <div style="margin-top:.75rem;padding-top:.75rem;border-top:1px solid #e5e7eb;font-size:.85rem;color:#374151;word-break:break-word">
      ${formatDate(a.startDate)} – ${formatDate(a.endDate)} · ${a.durationWeeks} Woche${a.durationWeeks !== 1 ? "n" : ""} · ${a.hoursPerWeek} Std./Woche
    </div>
  </div>`;
}

function renderUpcomingAssignment(a: StudentAssignment): string {
  return `<div data-assignment-id="${a.id}" style="display:flex;align-items:flex-start;gap:.75rem;padding:.75rem;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb${a.practiceId ? ";cursor:pointer" : ""}">
    <div style="width:4px;align-self:stretch;background:${a.departmentColor};border-radius:2px;flex-shrink:0"></div>
    <div style="flex:1;min-width:0">
      <div style="font-size:.8rem;color:${a.departmentColor};font-weight:600">${esc(a.departmentName)}</div>
      <div style="font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(a.practiceName || "Freier Eintrag")}</div>
      <div style="font-size:.8rem;color:#6b7280">${formatDate(a.startDate)} – ${formatDate(a.endDate)}</div>
    </div>
  </div>`;
}

function renderHoursOverview(hours: StudentHours[]): string {
  const totalRequired = hours.reduce((sum, h) => sum + h.targetHours, 0);
  const totalCompleted = hours.reduce((sum, h) => sum + h.completedHours, 0);
  const totalPct = totalRequired > 0 ? Math.min(Math.round((totalCompleted / totalRequired) * 100), 100) : 0;

  return `<div class="card" style="padding:1.25rem">
    <h3 style="font-size:.95rem;font-weight:600;margin:0 0 1rem">Stundenfortschritt</h3>
    <div style="display:flex;align-items:center;gap:1rem;margin-bottom:1.25rem">
      <div style="flex:1">
        ${renderProgressBar(totalCompleted, totalRequired, "#3b82f6")}
      </div>
      <div style="font-size:.875rem;font-weight:600;white-space:nowrap;color:#1d4ed8">${totalCompleted} / ${totalRequired} Std.</div>
    </div>
    <div style="display:flex;flex-direction:column;gap:.75rem">
      ${hours.map(h => `
        <div>
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:.3rem">
            <span style="font-size:.85rem;font-weight:500;color:#374151">${esc(h.nameDe)}</span>
            <span style="font-size:.8rem;color:#6b7280">${h.completedHours} / ${h.targetHours} Std.</span>
          </div>
          ${renderProgressBar(h.completedHours, h.targetHours, h.color)}
        </div>`).join("")}
    </div>
    <div style="margin-top:1rem;padding-top:.75rem;border-top:1px solid #e5e7eb;text-align:center;font-size:.875rem;color:#6b7280">
      Gesamtfortschritt: <strong>${totalPct}%</strong>
    </div>
  </div>`;
}

export async function render(el: HTMLElement) {
  el.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;

  try {
    const profil = await api.schuelerApp.profil();

    // Planung laden (optional, nur wenn Feature vorhanden und die Schule
    // den Menüpunkt "Meine Planung" nicht abgeschaltet hat)
    let planungData: StudentPlanungData | null = null;
    if (hasPortalFeature("meinePlanung")) {
      try { planungData = await api.schuelerApp.planung(); } catch { /* Feature nicht aktiv */ }
    }

    const currentAssignment = planungData?.assignments.find(a => a.isCurrent) || null;
    const upcomingAssignments = (planungData?.assignments.filter(a => a.isUpcoming) || []).slice(0, 3);
    const hours = planungData?.hours || [];

    el.innerHTML = `
      <div class="page-header">
        <div>
          <h1 style="margin-bottom:.25rem">Hallo, ${esc(profil.firstName)}!</h1>
          <p style="color:#6b7280;margin:0">${esc(profil.className ? `Klasse ${profil.className}` : "")} ${esc(profil.schoolName)}</p>
        </div>
        <div style="text-align:right;font-size:.875rem;color:#9ca3af">
          Heute: ${todayFormatted()}
        </div>
      </div>

      <div style="display:grid;gap:1.5rem;grid-template-columns:1fr">

        ${planungData ? `
        <!-- Aktueller Einsatz -->
        <section style="min-width:0">
          <h2 style="font-size:1rem;font-weight:600;margin-bottom:.75rem;color:#374151">Aktueller Einsatz</h2>
          ${currentAssignment
            ? renderCurrentAssignment(currentAssignment)
            : `<div class="card" style="padding:1.25rem;text-align:center;color:#9ca3af">
                <p>Kein aktueller Einsatz.</p>
               </div>`}
        </section>

        ${upcomingAssignments.length > 0 ? `
        <!-- Nächste Einsätze -->
        <section style="min-width:0">
          <h2 style="font-size:1rem;font-weight:600;margin-bottom:.75rem;color:#374151">Nächste Einsätze</h2>
          <div style="display:flex;flex-direction:column;gap:.5rem">
            ${upcomingAssignments.map(renderUpcomingAssignment).join("")}
          </div>
        </section>` : ""}

        <!-- Zeitraum-Hinweis -->
        ${planungData.period ? `<div style="font-size:.8rem;color:#9ca3af;text-align:center;padding:.25rem 0">
          Planung: <strong style="color:#6b7280">${esc(planungData.period.name)}</strong>
          &nbsp;·&nbsp; ${formatDate(planungData.period.startDate)} – ${formatDate(planungData.period.endDate)}
        </div>` : ""}
        ` : ""}

        <!-- Meine Daten -->
        <div class="card" style="padding:1.25rem">
          <h3 style="font-size:.95rem;font-weight:600;margin:0 0 .75rem">Meine Kontaktdaten</h3>
          <div class="student-contact-grid">
            ${profil.email ? `<div><span style="color:#6b7280">E-Mail:</span> ${esc(profil.email)}</div>` : ""}
            ${profil.phone ? `<div><span style="color:#6b7280">Telefon:</span> ${esc(profil.phone)}</div>` : ""}
            ${(profil.street || profil.plz || profil.ort) ? `
              <div style="grid-column:1/-1"><span style="color:#6b7280">Adresse:</span>
                ${[`${profil.street || ""} ${profil.houseNo || ""}`.trim(), `${profil.plz || ""} ${profil.ort || ""}`.trim()].filter(Boolean).join(", ")}
              </div>` : ""}
          </div>
        </div>

      </div>`;

    // Klick-Handler für Einsatz-Detail
    const allAssignments = [
      ...(planungData?.assignments || []),
      ...(planungData?.futurePeriods?.flatMap(fp => fp.assignments) || []),
    ];
    const assignmentMap = new Map<string, StudentAssignment>(allAssignments.map(a => [String(a.id), a]));
    el.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).closest("a")) return;
      const card = (e.target as HTMLElement).closest("[data-assignment-id]") as HTMLElement | null;
      if (!card) return;
      const a = assignmentMap.get(card.dataset.assignmentId!);
      if (a) showAssignmentDetail(a);
    });

  } catch (err: any) {
    el.innerHTML = `<div class="alert alert-error">Fehler beim Laden: ${esc(err.message)}</div>`;
  }
}
